/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * MDAQS (Audio quality) scores for the category page's lab dialog. Cards carry only the
 * overall axis, so the four metrics come from the product-line resolve — one read per
 * product per page load, cached here, started when the dialog opens so the tab is ready by
 * the time it is tapped.
 */
import { useEffect, useState } from 'react'
import { resolveDealHuntPage } from 'lib/dealhunt/client'
import { buildSoundGuysReview, type MdaqsScore } from 'lib/dealhunt/review'
import type { DealHuntMdaqs } from 'lib/dealhunt/types'

export interface MdaqsBundle {
  /** The chart panel's primary shape (`mdaqs_*` keys, score + pct). */
  scores: MdaqsScore[]
  /** The overlay shape (`DealHuntLabComparison.mdaqs`). */
  overlay: DealHuntMdaqs
}

/** The flat product slug the resolve wants, from a PDP href (`/product/apple-airpods-4/`). */
export function flatSlugFromHref(href: string | null | undefined): string | null {
  if (!href) {
    return null
  }
  const path = href.split(/[?#]/)[0]
  const segments = path.split('/').filter(Boolean)
  const last = segments[segments.length - 1]
  return segments[0] === 'product' && last ? last : null
}

export function toBundle(scores: MdaqsScore[]): MdaqsBundle | null {
  if (!scores.length) {
    return null
  }
  const pick = (key: string): number | null =>
    scores.find(s => s.key === `mdaqs_${key}`)?.score ?? null
  return {
    scores,
    overlay: {
      timbre: pick('timbre'),
      distortion: pick('distortion'),
      immersiveness: pick('immersiveness'),
      overall: pick('overall'),
    },
  }
}

const cache = new Map<string, Promise<MdaqsBundle | null>>()

export function getMdaqs(flatSlug: string): Promise<MdaqsBundle | null> {
  let pending = cache.get(flatSlug)
  if (!pending) {
    pending = resolveDealHuntPage([flatSlug])
      .then(page => {
        if (page?.kind !== 'product') {
          return null
        }
        return toBundle(buildSoundGuysReview(page.enrichment)?.mdaqs ?? [])
      })
      .catch(() => null)
    cache.set(flatSlug, pending)
  }
  return pending
}

/** `undefined` while loading (or not asked), `null` when the product has no MDAQS. */
export function useMdaqs(
  flatSlug: string | null,
  enabled: boolean
): MdaqsBundle | null | undefined {
  const [bundle, setBundle] = useState<MdaqsBundle | null | undefined>(undefined)
  useEffect(() => {
    if (!enabled || !flatSlug) {
      setBundle(flatSlug ? undefined : null)
      return undefined
    }
    let cancelled = false
    void getMdaqs(flatSlug).then(loaded => {
      if (!cancelled) {
        setBundle(loaded)
      }
    })
    return () => {
      cancelled = true
    }
  }, [flatSlug, enabled])
  return bundle
}
